import { useEffect, useMemo, useRef, useState } from "react";
import type { OverlayState } from "../../state/store";
import {
  archiveRadarFrameSet, futureFrameSet, globalWvFrameSet, lightningFrameSet, mesoFrameSet, ohcFrameSet, pickBird, pickRadarSource,
  pickSatSource, radarFrameSet, satFrameSet, satSourceKey, sstFrameSet, wvNeedsGlobal,
  type FrameSet, type FutureInfo, type RadarSource, type SatBird, type SatSource,
} from "./providers";

export interface ViewState {
  lng: number;
  lat: number;
  bbox: [number, number, number, number]; // w, s, e, n
}

const MIN = 60_000;

/**
 * Chooses the feeds for the current view and keeps their frame lists fresh.
 * Frame keys are stable, so a refresh only ever ADDS the newest frame (and
 * drops the oldest) in the engine — it never reloads the loop.
 * `replayTime` switches radar to the NEXRAD archive at that moment;
 * `stormPos` (the live storm's latest fix) anchors the 1-minute mesoscale loop;
 * `stormId` picks the model run the futurecast plays.
 */
export function useOverlayFrames(
  overlays: OverlayState,
  view: ViewState | null,
  replayTime: number | null,
  stormPos: { lat: number; lon: number } | null,
  stormId: string | null = null,
) {
  const [radar, setRadar] = useState<FrameSet | null>(null);
  const [sat, setSat] = useState<FrameSet | null>(null);
  const [satBase, setSatBase] = useState<FrameSet | null>(null);
  const [ocean, setOcean] = useState<FrameSet | null>(null);
  const [lightning, setLightning] = useState<FrameSet | null>(null);
  const [meso, setMeso] = useState<FrameSet | null>(null);
  const [mesoNote, setMesoNote] = useState<string | null>(null);
  const [future, setFuture] = useState<{ set: FrameSet; detail: FrameSet | null; info: FutureInfo } | null>(null);
  const [futureNote, setFutureNote] = useState<string | null>(null);
  const radarSrcRef = useRef<RadarSource | null>(null);
  const birdRef = useRef<SatBird | null>(null);
  const satSrcRef = useRef<SatSource | null>(null);
  const lastArchiveRef = useRef<FrameSet | null>(null);

  // feed selection follows the view, sticky at the edges (hysteresis lives in the pickers)
  const radarSource = useMemo(() => {
    if (!overlays.radar || !view) return null;
    radarSrcRef.current = pickRadarSource(view.lng, view.lat, radarSrcRef.current);
    return radarSrcRef.current;
  }, [overlays.radar, view]);

  const satSource = useMemo(() => {
    if (overlays.satellite === "off" || !view) return null;
    birdRef.current = pickBird(view.lng, birdRef.current);
    satSrcRef.current = pickSatSource(overlays.satellite, view.bbox, birdRef.current, satSrcRef.current);
    return satSrcRef.current;
  }, [overlays.satellite, view]);
  const satKey = satSource ? satSourceKey(satSource) : null;

  // ── radar: live feed, or the archive under a replay ──
  useEffect(() => {
    if (!overlays.radar) { setRadar(null); lastArchiveRef.current = null; return; }
    let cancelled = false;
    if (replayTime != null) {
      // debounce the TimeDock scrub; keep the previous hour's frame listed
      // FIRST so the engine holds it on screen until the new one has loaded
      const t = setTimeout(async () => {
        const next = await archiveRadarFrameSet(replayTime);
        if (cancelled) return;
        const prev = lastArchiveRef.current?.frames.at(-1);
        const merged = next && prev && prev.key !== next.frames[0].key
          ? { ...next, frames: [prev, ...next.frames] }
          : next;
        lastArchiveRef.current = next;
        setRadar(merged);
      }, 250);
      return () => { cancelled = true; clearTimeout(t); };
    }
    lastArchiveRef.current = null;
    if (!radarSource) return;
    const load = async () => {
      const set = await radarFrameSet(radarSource, overlays.motion ? overlays.loopMin : 30);
      if (!cancelled && set) setRadar(set);
    };
    void load();
    // IEM and nowCOAST publish every ~4–5 min, RainViewer every 10
    const iv = setInterval(load, (radarSource === "rainviewer" ? 5 : 2.5) * MIN);
    return () => { cancelled = true; clearInterval(iv); };
  }, [overlays.radar, overlays.motion, overlays.loopMin, radarSource, replayTime]);

  // ── satellite ──
  useEffect(() => {
    if (!satSource) { setSat(null); return; }
    let cancelled = false;
    const load = async () => {
      // static view still lists ~40 min so a not-yet-tiled newest frame has a fallback
      const set = await satFrameSet(satSource, overlays.motion ? overlays.loopMin : 40);
      if (!cancelled && set) setSat(set);
    };
    void load();
    const iv = setInterval(load, (satSource.kind === "nowcoast" ? 2.5 : 5) * MIN);
    return () => { cancelled = true; clearInterval(iv); };
    // satKey captures the source identity; satSource itself is a fresh object per view change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [satKey, overlays.motion, overlays.loopMin]);

  // ── water vapour past the GOES sectors: the hourly global mosaic underneath ──
  const wvGlobal = overlays.satellite === "wv" && !!view && wvNeedsGlobal(view.bbox);
  useEffect(() => {
    if (!wvGlobal) { setSatBase(null); return; }
    let cancelled = false;
    const load = async () => {
      const set = await globalWvFrameSet(overlays.motion ? overlays.loopMin : 40);
      if (!cancelled && set) setSatBase(set);
    };
    void load();
    const iv = setInterval(load, 10 * MIN); // hourly product, ~1 h latency
    return () => { cancelled = true; clearInterval(iv); };
  }, [wvGlobal, overlays.motion, overlays.loopMin]);

  // ── ocean: SST (6-hourly) or ocean heat content (daily) ──
  useEffect(() => {
    if (overlays.ocean === "off") { setOcean(null); return; }
    let cancelled = false;
    const load = async () => {
      const set = overlays.ocean === "sst" ? await sstFrameSet() : await ohcFrameSet();
      if (!cancelled) setOcean(set);
    };
    void load();
    const iv = setInterval(load, 60 * MIN);
    return () => { cancelled = true; clearInterval(iv); };
  }, [overlays.ocean]);

  // ── lightning: 15-min strike density ──
  useEffect(() => {
    if (!overlays.lightning) { setLightning(null); return; }
    let cancelled = false;
    const load = async () => {
      const set = await lightningFrameSet(overlays.motion ? overlays.loopMin : 15);
      if (!cancelled && set) setLightning(set);
    };
    void load();
    const iv = setInterval(load, 5 * MIN);
    return () => { cancelled = true; clearInterval(iv); };
  }, [overlays.lightning, overlays.motion, overlays.loopMin]);

  // ── 1-minute mesoscale loop over the live storm ──
  // the sector lookup follows the storm's latest fix, rounded so the 6-hourly
  // fix updates (not every re-render) are what trigger a new lookup
  const mesoLat = stormPos ? Math.round(stormPos.lat * 2) / 2 : null;
  const mesoLon = stormPos ? Math.round(stormPos.lon * 2) / 2 : null;
  const mesoProduct = overlays.satellite === "wv" || overlays.satellite === "vis" ? overlays.satellite : "ir";
  useEffect(() => {
    if (!overlays.meso || mesoLat == null || mesoLon == null) { setMeso(null); setMesoNote(null); return; }
    let cancelled = false;
    const load = async () => {
      const set = await mesoFrameSet(mesoLat, mesoLon, mesoProduct, overlays.motion ? Math.min(overlays.loopMin, 60) : 10);
      if (cancelled) return;
      setMeso(set);
      setMesoNote(set ? null : "No GOES mesoscale sector covers this storm right now");
    };
    setMesoNote("finding the mesoscale sector…");
    void load();
    const iv = setInterval(load, MIN); // a new scan lands every minute
    return () => { cancelled = true; clearInterval(iv); };
  }, [overlays.meso, overlays.motion, overlays.loopMin, mesoLat, mesoLon, mesoProduct]);

  // ── futurecast: a model's simulated IR / radar / rain along the future path ──
  useEffect(() => {
    if (!overlays.future || !stormId) { setFuture(null); setFutureNote(null); return; }
    let cancelled = false;
    const load = async () => {
      const r = await futureFrameSet(stormId, overlays.futureProduct, overlays.futureModel, overlays.futureChanceIn)
        .catch(() => ({ note: "Futurecast unavailable right now" }));
      if (cancelled) return;
      if ("set" in r) { setFuture(r); setFutureNote(null); } else { setFuture(null); setFutureNote(r.note); }
    };
    setFutureNote(`finding the latest ${overlays.futureModel === "wpc" ? "WPC forecast" : overlays.futureModel === "nbm" ? "NBM blend" : "model run"}…`);
    void load();
    const iv = setInterval(load, 30 * MIN); // a new run lands every 6 h
    return () => { cancelled = true; clearInterval(iv); };
  }, [overlays.future, overlays.futureProduct, overlays.futureModel, overlays.futureChanceIn, stormId]);

  return {
    radar: overlays.radar ? radar : null,
    sat: overlays.satellite !== "off" ? sat : null,
    satBase: wvGlobal ? satBase : null,
    ocean: overlays.ocean !== "off" ? ocean : null,
    lightning: overlays.lightning ? lightning : null,
    meso: overlays.meso ? meso : null,
    mesoNote: overlays.meso ? mesoNote : null,
    future: overlays.future ? future : null,
    futureNote: overlays.future ? futureNote : null,
    radarSource,
    satSource,
  };
}
